import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

type Props = {
  src: string;
  alt?: string;
  onClose: () => void;
};

/**
 * 画像を画面全体に大きく重ねて表示するライトボックス。
 * Esc / 背景クリック / ✕ ボタンで閉じる。
 */
export function ChatImageLightbox({ src, alt, onClose }: Props) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt || t('viewModel.aiChat.imagePreviewLabel')}
    >
      <button
        type="button"
        className="absolute right-4 top-4 text-2xl text-white/80 hover:text-white"
        aria-label={t('common.action.close')}
        onClick={(event) => {
          // オーバーレイの onClick へバブルして onClose が二重に呼ばれるのを防ぐ
          event.stopPropagation();
          onClose();
        }}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-full max-w-full object-contain"
        // 画像自体のクリックでは閉じない
        onClick={(event) => event.stopPropagation()}
      />
    </div>,
    document.body
  );
}
